import { Component, computed, inject, signal, TemplateRef, ViewChild } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { PageContentComponent } from '../page-content/page-content.component'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import { dateOnlyFormat, dateTimeWithoutTimezone, formatAsUTC } from 'src/app/shared/utilities/date'
import { ActiveFilterListComponent } from '../table/filters/active-filter-list/active-filter-list.component'
import { DataTableComponent } from '../table/data-table.component'
import { ColDef, GetRowIdFunc } from 'ag-grid-community'
import { catchError, combineLatest, from, NEVER, switchMap, tap } from 'rxjs'
import { DateRangeFilterModalComponent } from '../modals/filters/date-range-filter-modal.component'
import { withHeader } from '../table/headers/table-header.component'
import { TransformCellComponent } from '../table/cells/transform-cell.component'
import { EntityFilter } from 'src/app/shared/services/filter/entity-filter'
import { EntityFilterModalComponent } from '../modals/filters/entity-filter-modal.component'
import { ActivatedRoute, Router } from '@angular/router'
import { Location } from '@angular/common'
import { ToastService } from '../toasts/toasts.service'
import { MatMenuModule } from '@angular/material/menu'
import { TBillSettlementsService } from '../settlements/tbill-settlements.service'
import { PaginationParams } from 'src/app/shared/utilities/http-params'
import { TBillSPChargeCreateRequest, TBillSPCharge, TBillSPChargeUpdateRequest } from '../settlements/tbill-settlements.types'
import { TablePaginationComponent } from '../table/table-pagination/table-pagination.component'
import { TableSortService } from '../table/services/table-sort.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { StringFilterModalComponent } from '../modals/filters/string-filter-modal.component'
import { StringFilter } from 'src/app/shared/services/filter/string-filter'
import openModal from 'src/app/shared/services/openModal'
import { CreateTBillSPChargeModalComponent } from './create-tbill-sp-charge-modal.component'
import { UpdateTBillSPChargeModalComponent } from './update-tbill-sp-charge-modal.component'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { EntityCellComponent } from '../table/cells/entity-cell.component'
import { actionColumn, withCell } from '../table/cells/with-cell'
import { pipe } from 'lodash/fp'
import { DateCellComponent } from '../table/cells/date-cell.component'
import { NON_TERMINATING_NAMED_DATE } from 'src/app/shared/models/namedDate'
import { LabeledDisplayComponent } from '../labeled-display/labeled-display.component'
import { DateDisplayComponent } from '../date-display/date-display.component'

@Component({
  selector: 'app-tbill-sp-charges',
  imports: [
    MatMenuModule,
    PageHeaderComponent,
    PageContentComponent,
    LabeledPropertyComponent,
    ActiveFilterListComponent,
    DataTableComponent,
    TablePaginationComponent,
    LabeledDisplayComponent,
    DateDisplayComponent,
  ],
  providers: [TableSortService],
  styles: '',
  template: `
    <div app-page-header header="TBill SP Charges" [menuItems]="pageHeaderMenuItems"></div>
    <div app-page-content>
        <app-active-filter-list [filterGroup]="constructor.name"></app-active-filter-list>
        <app-data-table [loading]="loading()" [data]="data()" [getRowId]="getRowId" [columnDefs]="columnDefs"></app-data-table>
        <app-table-pagination
            [pageNumber]="pageNumber()"
            [pageSize]="pageSize()"
            [totalResults]="totalResults()"
            (pageNumberChange)="pageNumber.set($event)"
            (pageSizeChange)="pageSize.set($event)"
        />
    </div>

    <ng-template #deleteConfirmation let-ctx="ctx">
        Are you sure you want to delete this TBill SP Charge?
        @if(ctx) {
          <div app-labeled-property label="Retailer">
            <div app-labeled-display
              [label]="ctx.retailer_name" 
              [value]="ctx.retailer_code">
            </div>
          </div>
          <div app-labeled-property label="Charge Type">
            <div app-labeled-display
              [label]="ctx.tbill_sp_charge_type_name" 
              [value]="ctx.tbill_sp_charge_type_code">
            </div>
          </div>
          <div app-labeled-property label="Charge Method">
            <div app-labeled-display
              [label]="ctx.charge_method_name" 
              [value]="ctx.charge_method_code">
            </div>
          </div>
          <div app-labeled-property label="Effective Date">
            <span app-date-display [value]="ctx.effective_date"></span>
          </div>
          <div app-labeled-property label="Termination Date">
            <span app-date-display [value]="ctx.termination_date" [namedDates]="namedTerminationDates"></span>
          </div>
        }
    </ng-template>
    `,
})
export class TBillSPChargesComponent {
  protected readonly dateOnlyFormat = dateOnlyFormat
  protected readonly namedTerminationDates = [NON_TERMINATING_NAMED_DATE]

  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly location = inject(Location)
  private readonly toastService = inject(ToastService)
  private readonly filterService = inject(FilterService)
  private readonly sortService = inject(TableSortService)
  private readonly modalService = inject(NgbModal)
  private readonly tbillSettlementsService = inject(TBillSettlementsService)

  @ViewChild('deleteConfirmation')
  deleteConfirmationTemplate: TemplateRef<{ ctx: TBillSPCharge }>

  protected readonly refresh = signal(0)
  protected readonly loading = signal(false)
  protected readonly pageSize = signal(25)
  protected readonly pageNumber = signal(1)
  protected readonly totalResults = signal(0)
  protected readonly data = signal<TBillSPCharge[]>([])
  protected readonly getRowId: GetRowIdFunc<TBillSPCharge> = (data) => [
    data.data.retailer_uid,
    data.data.tbill_sp_charge_type_uid,
    formatAsUTC(data.data.effective_date),
  ].join('|')
  protected readonly paginationParams = computed<PaginationParams>(() => ({
    pageNumber: this.pageNumber(),
    pageSize: this.pageSize(),
  }))

  protected readonly pageHeaderMenuItems: readonly PageHeaderMenuItem[] = [
    { label: 'Create', onClick: () => this.openCreateModal() },
  ]

  protected readonly columnDefs: ColDef[] = [
    pipe(
      withHeader<TBillSPCharge, EntityFilterModalComponent>({
        headerName: 'Retailer',
        field: 'retailer_code',
        filterable: true,
        sortable: true,
        filterGroup: TBillSPChargesComponent.name,
        modal: EntityFilterModalComponent,
        modalInit: {},
      }),
      withCell<EntityCellComponent<TBillSPCharge, 'retailer_code'>>(
        EntityCellComponent,
        {
          label: (d) => d.retailer_name,
          value: (d) => d.retailer_code,
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 150,
    }),
    pipe(
      withHeader<TBillSPCharge, EntityFilterModalComponent>({
        headerName: 'Charge Type',
        field: 'tbill_sp_charge_type_code',
        filterable: true,
        sortable: true,
        filterGroup: TBillSPChargesComponent.name,
        modal: EntityFilterModalComponent,
        modalInit: {},
      }),
      withCell<EntityCellComponent<TBillSPCharge, 'tbill_sp_charge_type_code'>>(
        EntityCellComponent,
        {
          label: (d) => d.tbill_sp_charge_type_name,
          value: (d) => d.tbill_sp_charge_type_code,
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 150,
    }),
    pipe(
      withHeader<TBillSPCharge, DateRangeFilterModalComponent>({
        headerName: 'Effective Date',
        field: 'effective_date',
        filterable: true,
        sortable: true,
        filterGroup: TBillSPChargesComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillSPCharge>>(
        DateCellComponent,
        {
          date: (d) => d.effective_date,
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 180,
    }),
    pipe(
      withHeader<TBillSPCharge, DateRangeFilterModalComponent>({
        headerName: 'Termination Date',
        field: 'termination_date',
        filterable: true,
        sortable: true,
        filterGroup: TBillSPChargesComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillSPCharge>>(
        DateCellComponent,
        {
          date: (d) => d.termination_date,
          namedDates: this.namedTerminationDates,
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 180,
    }),
    pipe(
      withHeader<TBillSPCharge, EntityFilterModalComponent>({
        headerName: 'Charge Method',
        field: 'charge_method_code',
        filterable: true,
        sortable: true,
        filterGroup: TBillSPChargesComponent.name,
        modal: EntityFilterModalComponent,
        modalInit: {},
      }),
      withCell<EntityCellComponent<TBillSPCharge, 'charge_method_code'>>(
        EntityCellComponent,
        {
          label: (d) => d.charge_method_name,
          value: (d) => d.charge_method_code,
        },
      ),
    )({
      editable: false,
      minWidth: 100,
      width: 150,
    }),
    pipe(
      withHeader<TBillSPCharge>({
        headerName: 'Template Values',
        field: 'charge_template_values',
        filterGroup: TBillSPChargesComponent.name,
      }),
      withCell<TransformCellComponent<TBillSPCharge>>(
        TransformCellComponent,
        {
          transformData: (d) => Object.entries(d.charge_template_values).map(([k, v]) => `${k}=${v}`).join(', '),
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 200,
    }),
    pipe(
      withHeader<TBillSPCharge>({
        headerName: 'Charge Description Override',
        field: 'charge_description_override',
        filterGroup: TBillSPChargesComponent.name,
      }),
      withCell<TransformCellComponent<TBillSPCharge>>(
        TransformCellComponent,
        {
          transform: (v) => v + '',
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 250,
    }),
    pipe(
      withHeader<TBillSPCharge, DateRangeFilterModalComponent>({
        headerName: 'Created On (STL)',
        field: 'create_time',
        filterable: true,
        sortable: true,
        filterGroup: TBillSPChargesComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillSPCharge>>(
        DateCellComponent,
        {
          date: (d) => d.create_time,
          format: dateTimeWithoutTimezone,
          viewAsTZ: 'America/Chicago',
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillSPCharge, StringFilterModalComponent>({
        headerName: 'Created By',
        field: 'created_by',
        filterable: true,
        sortable: true,
        filterGroup: TBillSPChargesComponent.name,
        modal: StringFilterModalComponent,
        modalInit: {},
      }),
    )({
      editable: false,
      minWidth: 100,
      width: 200,
    }),
    pipe(
      withHeader<TBillSPCharge, DateRangeFilterModalComponent>({
        headerName: 'Last Updated (STL)',
        field: 'last_update_time',
        filterable: true,
        sortable: true,
        filterGroup: TBillSPChargesComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillSPCharge>>(
        DateCellComponent,
        {
          date: (d) => d.last_update_time,
          format: dateTimeWithoutTimezone,
          viewAsTZ: 'America/Chicago',
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillSPCharge, StringFilterModalComponent>({
        headerName: 'Last Updated By',
        field: 'last_updated_by',
        filterable: true,
        sortable: true,
        filterGroup: TBillSPChargesComponent.name,
        modal: StringFilterModalComponent,
        modalInit: {},
      }))({
      editable: false,
      minWidth: 100,
      width: 200,
    }),
    actionColumn<TBillSPCharge>(TBillSPChargesComponent.name, {
      actions: [
        {
          name: 'Update',
          onAction: (d: TBillSPCharge) => this.openUpdateModal(d),
        },
        {
          name: 'Delete',
          onAction: (d: TBillSPCharge) => this.openDeleteModal(d),
        },
      ],
    }),
  ]

  constructor() {
    const dispatch = this.filterService.dispatch.bind(this.filterService) as (a: unknown) => void
    this.filterService.dispatch(FilterService.InitFiltersAction({
      fieldGroup: TBillSPChargesComponent.name,
      fields: {
        retailer_code: new EntityFilter({
          config: {
            resource: 'retailers',
            allowMultiple: true,
            allowWildcards: false,
            meta: {
              fieldGroup: TBillSPChargesComponent.name,
              fieldName: 'retailer_code',
              fieldDisplay: 'Retailer',
            },
          },
        }, dispatch),
        tbill_sp_charge_type_code: new EntityFilter({
          config: {
            resource: 'tbillSPChargeTypes',
            allowMultiple: true,
            allowWildcards: false,
            meta: {
              fieldGroup: TBillSPChargesComponent.name,
              fieldName: 'tbill_sp_charge_type_code',
              fieldDisplay: 'Charge Type',
            },
          },
        }, dispatch),
        charge_method_code: new EntityFilter({
          config: {
            resource: 'tbillSPChargeMethods',
            allowMultiple: true,
            allowWildcards: false,
            meta: {
              fieldGroup: TBillSPChargesComponent.name,
              fieldName: 'charge_method_code',
              fieldDisplay: 'Charge Method',
            },
          },
        }, dispatch),
        effective_date: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            sendTime: true,
            onlyMonth: false,
            meta: {
              fieldGroup: TBillSPChargesComponent.name,
              fieldName: 'effective_date',
              fieldDisplay: 'Effective Date',
            },
          },
        }, dispatch),
        termination_date: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            sendTime: true,
            onlyMonth: false,
            meta: {
              fieldGroup: TBillSPChargesComponent.name,
              fieldName: 'termination_date',
              fieldDisplay: 'Termination Date',
            },
          },
        }, dispatch),
        create_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: {
              fieldGroup: TBillSPChargesComponent.name,
              fieldName: 'create_time',
              fieldDisplay: 'Created On (STL)',
            },
          },
        }, dispatch),
        created_by: new StringFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: TBillSPChargesComponent.name,
              fieldName: 'created_by',
              fieldDisplay: 'Created By',
            },
          },
        }, dispatch),
        last_update_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: {
              fieldGroup: TBillSPChargesComponent.name,
              fieldName: 'last_update_time',
              fieldDisplay: 'Last Updated (STL)',
            },
          },
        }, dispatch),
        last_updated_by: new StringFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: TBillSPChargesComponent.name,
              fieldName: 'last_updated_by',
              fieldDisplay: 'Last Updated By',
            },
          },
        }, dispatch),
      },
    }))

    combineLatest([
      toObservable(this.paginationParams),
      this.filterService
        .asParams(TBillSPChargesComponent.name)
        .pipe(
          tap(() => {
            if (this.pageNumber() !== 1) {
              this.pageNumber.set(1)
            }
          }),
        ),
      this.sortService.getOrder(),
      toObservable(this.refresh),
    ])
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loading.set(true)),
        switchMap(([pagination, filters, order]) => this.tbillSettlementsService.getTBillSPCharges(pagination, filters, order)),
        catchError(this.handleLoadingError('Something went wrong loading tbill retailer usage overrides!')),
      )
      .subscribe({
        error: this.handleLoadingError('Something went wrong loading tbill retailer usage overrides!'),
        next: (res) => {
          this.totalResults.set(res.total)
          this.data.set(res.results)
          this.loading.set(false)
        },
      })

    from(this.filterService.manageQueryParms(
      this.router,
      this.route,
      this.location,
      TBillSPChargesComponent.name,
    ))
      .pipe(
        switchMap(obs$ => obs$),
        takeUntilDestroyed(),
      )
      .subscribe()
  }

  protected handleLoadingError(defaultMessage: string){
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (e: any) => {
      this.data.set([])
      this.loading.set(false)
      this.toastService.show({ type: 'error', message: e.detail ?? defaultMessage })
      return NEVER
    }
  }

  async openCreateModal() {
    openModal(
      this.modalService,
      CreateTBillSPChargeModalComponent,
      { centered: true },
    ).init({
      modalState: {},
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onCreate: this.onCreate.bind(this),
    })
  }

  async onCreate(req: TBillSPChargeCreateRequest) {
    this.tbillSettlementsService.createTBillSPCharge(req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong creating this SP Charge!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Creation successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }

  async openUpdateModal(data: TBillSPCharge) {
    openModal(
      this.modalService,
      UpdateTBillSPChargeModalComponent,
      { centered: true },
    ).init({
      data,
      modalState: {
        termination_date: { operation: 'eq', value: data.termination_date },
        charge_method: { entity_code: data.charge_method_code, entity_name: data.charge_method_name, entity_uid: data.charge_method_uid },
        charge_description_override: data.charge_description_override,
        charge_template_values: Object.entries(data.charge_template_values).map(([key, value]) => ({ key, value: Number(value) })),
      },
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onUpdate: this.onUpdate.bind(this),
    })
  }

  async onUpdate(retailer_uid: string, tbill_sp_charge_type_uid: string, effective_date: Date, req: TBillSPChargeUpdateRequest) {
    this.tbillSettlementsService.updateTBillSPCharge(retailer_uid, tbill_sp_charge_type_uid, effective_date, req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong updating this charge!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Update successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }

  async openDeleteModal(data: TBillSPCharge) {
    openModal(
      this.modalService,
      ConfirmationModalComponent,
      { centered: true },
    ).init({
      headerText: 'Confirm Deletion',
      confirmButtonText: 'Delete',
      bodyTemplate: this.deleteConfirmationTemplate,
      bodyContext: data,
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onConfirm: () => this.onDelete(data.retailer_uid, data.tbill_sp_charge_type_uid, data.effective_date),
    })
  }

  async onDelete(retailer_uid: string, tbill_sp_charge_type_uid: string, effective_date: Date) {
    this.tbillSettlementsService.deleteTBillSPCharge(retailer_uid, tbill_sp_charge_type_uid, effective_date)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong deleting this charge!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Deletion successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }
}

